import { LocalizedLink as Link } from '@/components/LocalizedLink';
import { LogPulseLogo } from '@/components/LogPulseLogo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LanguageSelector } from '@/components/LanguageSelector';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useT } from '@/i18n/provider';

function LegalHeader() {
  const t = useT();
  return (
    <header className="sticky top-0 z-40 glass border-b border-gray-200/60 dark:border-gray-800/60">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-2">
            <LogPulseLogo className="h-6 w-6" />
            <span className="font-bold text-gray-900 dark:text-white">LogPulse</span>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSelector />
            <ThemeToggle />
            <a
              href="https://app.logpulse.io/sign-in"
              className="hidden sm:inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors"
            >
              {t('nav.getStarted')}
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}

export function ImprintPage() {
  const t = useT();

  useDocumentMeta({
    title: t('meta.imprint.title'),
    description: t('meta.imprint.description'),
  });

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <LegalHeader />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          {t('imprint.title')}
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-10">
          {t('imprint.lastUpdated')}
        </p>

        <div className="prose-legal space-y-8">
          {/* TODO(gianno): fill in legal entity details before launch */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              {t('imprint.operator.title')}
            </h2>
            <div className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed space-y-2 whitespace-pre-line">
              {t('imprint.operator.content')}
            </div>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              {t('imprint.contact.title')}
            </h2>
            <div className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed space-y-2 whitespace-pre-line">
              {t('imprint.contact.content')}
            </div>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              {t('imprint.registration.title')}
            </h2>
            <div className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed space-y-2 whitespace-pre-line">
              {t('imprint.registration.content')}
            </div>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              {t('imprint.founder.title')}
            </h2>
            <div className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed space-y-2 whitespace-pre-line">
              {t('imprint.founder.content')}
            </div>
          </section>
        </div>

        {/* Footer nav */}
        <div className="mt-16 pt-8 border-t border-gray-200 dark:border-gray-800 flex flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400">
          <Link to="/" className="hover:text-gray-900 dark:hover:text-white transition-colors">Home</Link>
          <Link to="/privacy" className="hover:text-gray-900 dark:hover:text-white transition-colors">{t('footer.privacy')}</Link>
          <Link to="/terms" className="hover:text-gray-900 dark:hover:text-white transition-colors">{t('footer.terms')}</Link>
          <Link to="/cookies" className="hover:text-gray-900 dark:hover:text-white transition-colors">{t('footer.cookies')}</Link>
          <Link to="/imprint" className="text-gray-900 dark:text-white font-medium">{t('footer.imprint')}</Link>
        </div>
      </main>
    </div>
  );
}
